import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DataFormActionsComponent,
  DataFormCardComponent,
  DataFormGridComponent,
  ErrorBlockComponent,
} from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CompanyMailSettingsDto } from '../data-access/settings.models';
import { SettingsService } from '../data-access/settings.service';
import { SETTINGS_TAB_FORM_STYLES } from './settings-tab-form.styles';
import { CompanyScopedSettingsTabBase } from './settings-tab-state.base';

@Component({
  selector: 'app-mail-settings-tab',
  standalone: true,
  imports: [
    DataFormActionsComponent,
    DataFormCardComponent,
    DataFormGridComponent,
    ErrorBlockComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-data-form-card>
      <h3>Mail Settings</h3>
      <p class="settings-tab__hint">
        {{ contextText }}
      </p>

      <form [formGroup]="mailForm" (ngSubmit)="saveMail()">
        @if (companyId) {
          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="useGlobalDefaults" />
            Use platform mail settings
          </label>
        }
        <fieldset [disabled]="!!companyId && mailForm.controls.useGlobalDefaults.value" class="mail-settings-fields">
        <lib-data-form-grid>
          <label>
            SMTP host
            <input type="text" formControlName="smtpHost" />
          </label>

          <label>
            SMTP port
            <input type="number" formControlName="smtpPort" min="1" max="65535" />
          </label>

          <label>
            SMTP user
            <input type="text" formControlName="smtpUser" />
          </label>

          <label>
            SMTP password
            <input type="password" formControlName="smtpPassword" autocomplete="new-password"
              [placeholder]="hasPassword ? 'Password configured' : ''" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="clearPassword" />
            Clear stored password
          </label>

          <label>
            SMTP encryption
            <input type="text" formControlName="smtpEncryption" />
          </label>

          <label>
            From email
            <input type="email" formControlName="smtpFromEmail" />
          </label>

          <label>
            From name
            <input type="text" formControlName="smtpFromName" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="isActive" />
            Mailer active
          </label>
        </lib-data-form-grid>
        </fieldset>

        <lib-data-form-actions>
          <button type="submit" [disabled]="(!usesGlobalDefaults && mailForm.invalid) || !canSaveSettings">
            {{ isSaving ? 'Saving...' : 'Save mail settings' }}
          </button>
        </lib-data-form-actions>
      </form>

      <form [formGroup]="mailTestForm" (ngSubmit)="sendTestMail()" class="settings-tab__mail-test">
        <lib-data-form-grid>
          <label>
            Test recipient email
            <input type="email" formControlName="toEmail" />
          </label>
          <label>
            Subject
            <input type="text" formControlName="subject" />
          </label>
          <label class="settings-tab__wide">
            Message
            <input type="text" formControlName="message" />
          </label>
        </lib-data-form-grid>

        <lib-data-form-actions>
          <button type="submit" [disabled]="mailTestForm.invalid || isSendingMailTest || !canSaveSettings">
            {{ isSendingMailTest ? 'Sending...' : 'Send test email' }}
          </button>
        </lib-data-form-actions>
      </form>

      @if (errorText) {
        <lib-error-block variant="error" [message]="(errorText | t) ?? ''" />
      }

      @if (successText) {
        <lib-error-block variant="success" [message]="successText" />
      }
    </lib-data-form-card>
  `,
  styles: [SETTINGS_TAB_FORM_STYLES, '.mail-settings-fields { border: 0; padding: 0; margin: 0; min-width: 0; }'],
})
export class MailSettingsTabComponent extends CompanyScopedSettingsTabBase implements OnInit
{
  @Input() public contextText = '';

  private readonly settingsService = inject(SettingsService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly mailForm = this.formBuilder.group({
    useGlobalDefaults: this.formBuilder.nonNullable.control(false),
    clearPassword: this.formBuilder.nonNullable.control(false),
    smtpHost: this.formBuilder.nonNullable.control('', [Validators.required]),
    smtpPort: this.formBuilder.nonNullable.control(587, [
      Validators.required,
      Validators.min(1),
      Validators.max(65535),
    ]),
    smtpUser: this.formBuilder.nonNullable.control(''),
    smtpPassword: this.formBuilder.nonNullable.control(''),
    smtpEncryption: this.formBuilder.nonNullable.control('tls'),
    smtpFromEmail: this.formBuilder.nonNullable.control('', [Validators.email]),
    smtpFromName: this.formBuilder.nonNullable.control(''),
    isActive: this.formBuilder.nonNullable.control(true),
  });

  protected readonly mailTestForm = this.formBuilder.group({
    toEmail: this.formBuilder.nonNullable.control('', [Validators.required, Validators.email]),
    subject: this.formBuilder.nonNullable.control('Clinic Platform SMTP Test'),
    message: this.formBuilder.nonNullable.control('This is a test email from Clinic Platform.'),
  });

  protected isSendingMailTest = false;
  protected hasPassword = false;

  protected get usesGlobalDefaults(): boolean {
    return Boolean(this.companyId && this.mailForm.controls.useGlobalDefaults.value);
  }

  public ngOnInit(): void
  {
    this.reloadSettings();
  }

  protected saveMail(): void
  {
    if ((!this.usesGlobalDefaults && this.mailForm.invalid) || !this.canSaveSettings) {
      return;
    }

    this.beginSaving();

    const value = this.mailForm.getRawValue();

    if (!this.usesGlobalDefaults && value.clearPassword && value.smtpPassword) {
      this.setError('Choose either a new password or clearing the stored password');
      this.finishSaving();
      return;
    }

    this.currentSettingsRequest(this.settingsService.updateMailSettings(this.usesGlobalDefaults ? { useGlobalDefaults: true } : {
      useGlobalDefaults: false,
      smtpHost: value.smtpHost.trim(),
      smtpPort: value.smtpPort,
      smtpUser: value.smtpUser.trim() || null,
      smtpPassword: value.clearPassword ? null : (value.smtpPassword || undefined),
      smtpEncryption: value.smtpEncryption.trim() || null,
      smtpFromEmail: value.smtpFromEmail.trim() || null,
      smtpFromName: value.smtpFromName.trim() || null,
      isActive: value.isActive,
    }, this.companyId)).subscribe({
      next: (settings) =>
      {
        this.applyMailSettings(settings);
        this.setSuccess('Mail settings saved');
        this.finishSaving();
      },
      error: (error: unknown) =>
      {
        this.setHttpError(error);
        this.finishSaving();
      },
    });
  }

  protected sendTestMail(): void
  {
    if (this.mailTestForm.invalid || this.isSendingMailTest || !this.canSaveSettings) {
      return;
    }

    const value = this.mailTestForm.getRawValue();
    this.isSendingMailTest = true;
    this.clearFeedback();

    this.currentSettingsRequest(this.settingsService.sendTestMail(
      {
        toEmail: value.toEmail.trim(),
        subject: value.subject.trim() || undefined,
        message: value.message.trim() || undefined,
      },
      this.companyId,
    )).subscribe({
      next: () => {
        this.setSuccess('Test email sent');
        this.isSendingMailTest = false;
      },
      error: (error: unknown) => {
        this.setHttpError(error);
        this.isSendingMailTest = false;
      },
    });
  }

  protected reloadSettings(): void
  {
    this.hasPassword = false;
    this.isSendingMailTest = false;
    this.mailTestForm.reset();

    this.loadSettings(this.settingsService.getMailSettings(this.companyId), this.mailForm).subscribe({
      next: (settings: CompanyMailSettingsDto | null) => this.applyMailSettings(settings),
      error: (error: unknown) =>
      {
        this.setHttpError(error);
      },
    });
  }

  private applyMailSettings(settings: CompanyMailSettingsDto | null): void {
    this.hasPassword = settings?.source !== 'global_default' && (settings?.hasPassword ?? false);
    if (!settings) {
      this.mailForm.reset({
        useGlobalDefaults: Boolean(this.companyId),
        clearPassword: false,
        smtpHost: '',
        smtpPort: 587,
        smtpUser: '',
        smtpPassword: '',
        smtpEncryption: 'tls',
        smtpFromEmail: '',
        smtpFromName: '',
        isActive: true,
      });
      return;
    }

    this.mailForm.patchValue({
      useGlobalDefaults: settings.source === 'global_default',
      clearPassword: false,
      smtpHost: settings.smtpHost,
      smtpPort: settings.smtpPort,
      smtpUser: settings.smtpUser ?? '',
      smtpPassword: '',
      smtpEncryption: settings.smtpEncryption ?? '',
      smtpFromEmail: settings.smtpFromEmail ?? '',
      smtpFromName: settings.smtpFromName ?? '',
      isActive: settings.isActive,
    });
  }
}
